import { useReduxState } from 'hooks';
import { EditorBlockTranslationReference } from 'components/editor';
import { RichTextValue } from 'components/common';
import {
  EditorBlockConverter,
  EditorBlockCustomControls,
  EditorBlockTextField
} from 'components/editor/blocks';
import { TextEditor } from 'components/editor/inputs';

import type { BlockQuote } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';

type StateProps = Pick<
  EditorStateProps,
  'editorLanguageActive' | 'editorLanguageReference'
>;

const EditorBlockQuote = ({
  blockId,
  data,
  isAIPreview,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockQuote> & StateProps) => {
  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const { messages, theme } = useReduxState();

  const handleAttributionChange = (newAttribution: string) => {
    const newLocales = {
      ...data.locales,
      [languageVersion]: {
        ...data.locales[languageVersion],
        translatable: {
          ...data.locales[languageVersion].translatable,
          attribution: newAttribution
        }
      }
    };
    updateData({ ...data, locales: newLocales });
  };

  const handleQuoteChange = (html: string) => {
    const newLocales = {
      ...data.locales,
      [languageVersion]: {
        ...data.locales[languageVersion],
        translatable: {
          ...data.locales[languageVersion].translatable,
          text: html
        }
      }
    };
    updateData({ ...data, locales: newLocales });
  };

  return (
    <>
      <div className="quote">
        <div className="text">
          {isAIPreview || isPreview ? (
            <RichTextValue
              html={data.locales[languageVersion].translatable.text}
            />
          ) : (
            <>
              <EditorBlockTranslationReference
                isInnerElement={true}
                referenceHtml={
                  data.locales[editorLanguageReference]?.translatable?.text ||
                  ''
                }
                translationHtml={
                  data.locales[languageVersion].translatable.text
                }
              >
                <TextEditor
                  content={data.locales[languageVersion].translatable.text}
                  onChange={handleQuoteChange}
                  padding="0"
                  placeholder={messages.editorBlockQuoteQuotePlaceholder}
                />
              </EditorBlockTranslationReference>
            </>
          )}
        </div>
        <>
          <EditorBlockTranslationReference
            isInnerElement={true}
            referenceHtml={
              data.locales[editorLanguageReference]?.translatable
                ?.attribution || ''
            }
            style={{ marginTop: 15, marginBottom: -5 }}
            translationHtml={
              data.locales[editorLanguageReference]?.translatable
                ?.attribution || ''
            }
          >
            <div
              className={`attribution ${
                isAIPreview || isPreview ? 'preview' : ''
              }`}
            >
              <div className="attribution-form">
                {isAIPreview || isPreview ? (
                  <RichTextValue
                    html={
                      data.locales[languageVersion].translatable.attribution
                    }
                  />
                ) : (
                  <EditorBlockTextField
                    onValueChange={handleAttributionChange}
                    placeholder={
                      messages.editorBlockQuoteAttributionPlaceholder
                    }
                    value={
                      data.locales[languageVersion].translatable.attribution
                    }
                  />
                )}
              </div>
            </div>
          </EditorBlockTranslationReference>
        </>

        <style jsx>{`
          .quote {
            border-left: 5px solid ${theme.lightGrey};
            color: ${theme.textColorLight};
            padding: 10px 20px;
            position: relative;
          }
          .attribution {
            display: flex;
            margin-top: 5px;
          }
          .attribution::before {
            content: '—';
            position: relative;
            top: 4px;
          }
          .attribution.preview::before {
            margin-right: 8px;
            top: -2px;
          }
          .attribution-form {
            flex-grow: 1;
          }
          .attribution :global(input) {
            border: 0;
            color: inherit;
          }
        `}</style>
      </div>

      {!isAIPreview && !isPreview && !editorLanguageActive ? (
        <EditorBlockCustomControls>
          <EditorBlockConverter
            blockId={blockId}
            blockType="quote"
            data={data}
            languageVersion={languageVersion}
          />
        </EditorBlockCustomControls>
      ) : null}
    </>
  );
};

export default EditorBlockQuote;
